import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { Tooltip } from 'src/components/overlay/tooltip';
import { cn } from 'src/utils/cn';

export const FieldLabel: FC<{
  name?: string;
  displayLabel: string;
  fieldId: string;
  info?: ReactNode;
  showError?: boolean;
  nested?: boolean;
}> = ({ name, displayLabel, fieldId, info, showError, nested }) => {
  return (
    <div>
      <Label
        htmlFor={fieldId}
        error={showError}
        nested={nested}
        data-testid={name ? `${name}Label` : undefined}
      >
        {displayLabel}
      </Label>

      {info && (
        // We use <NoWrap> to keep the label and info icon on the same line.
        // The &nbsp; adds space between them without allowing a line break.
        // This prevents the icon from moving to the next line when the label is long.
        <span className="whitespace-nowrap">
          &nbsp;
          <Tooltip content={info} dataTestId={name ? `${name}Info` : undefined}>
            {(targetProps) => (
              <FontAwesomeIcon
                {...targetProps}
                icon={faInfoCircle}
                // Alignment hack to vertically center the icon with the label
                // Basically, set the direct children all align to the baseline (default behaviour unless it's set otherwise)
                // but the icon, we align the icon to the text-top, which is the only way
                // we get it to be rendered exactly for all of its instances — only then,
                // we shift its position by 0.125rem from the top to align it with the label
                // note the translate instead of positioning relatively so we don't affect the parent's layout
                // .fa has a -0.125rem of vertical-align so we kinda "unset" it with this
                className="translate-y-[0.125rem] align-text-top!"
              />
            )}
          </Tooltip>
        </span>
      )}
    </div>
  );
};

export const Label: FC<
  {
    error?: boolean;
    nested?: boolean;
  } & ComponentPropsWithoutRef<'label'>
> = ({ error, nested, ...props }) => {
  return (
    <label
      {...props}
      className={cn(
        `text-label-base font-label-semibold`,
        error && 'text-foreground-support-error',
        nested &&
          // padding-left to align with checkbox
          props.className
      )}
    />
  );
};
